import responsiveImages from "./responsiveImages.json";
import { localData } from "../../api/localData";
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Fade,
  Container,
  useMediaQuery,
  useTheme,
  IconButton,
  Card,
  CardActionArea,
} from "@mui/material";
import { NavLink } from "react-router-dom";
import {
  ArrowForward,
  ArrowOutward,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";

const COLORS = {
  primary: "#123465",
  secondary: "#f9a836",
  white: "#ffffff",
  dark: "#091a33",
  navy: "#0d2344",
  softBg: "#eef4fb",
  softBorder: "rgba(18,52,101,0.10)",
  muted: "rgba(9,26,51,0.72)",
};

const LOCAL_PREVIEW_SOLUTIONS = [
  {
    id: "preview-industrie",
    titre: "Industrie & production",
    image: "/images/HomeSolution/industrie-usine-production.webp",
    slug: "/solutions",
  },
  {
    id: "preview-sante",
    titre: "Sante & laboratoire",
    image: "/images/HomeSolution/labo-sante-pharmacie-tracabilite.webp",
    slug: "/solutions",
  },
  {
    id: "preview-logistique",
    titre: "Logistique & entrepot",
    image: "/images/HomeSolution/logistique-tracabilite-inventaire-transport.webp",
    slug: "/solutions",
  },
  {
    id: "preview-distribution",
    titre: "Distribution & retail",
    image: "/images/HomeSolution/retail-mask-tablette-point-de-vente.webp",
    slug: "/solutions",
  },
  {
    id: "preview-services",
    titre: "Services publics",
    image: "/images/HomeSolution/services-publics.webp",
    slug: "/solutions",
  },
  {
    id: "preview-banque",
    titre: "Banque & services",
    image: "/images/HomeSolution/banque-services-terciaire-assurance.webp",
    slug: "/solutions",
  },
];

const solutionArticles = localData.articles.filter((item) => item.type === 0);

export default function HomeSolution() {
  const articles = solutionArticles;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const displayArticles = articles.length > 0 ? articles : LOCAL_PREVIEW_SOLUTIONS;
  const topSolutions = displayArticles.slice(0, 6);
  const carouselSolutions = [
    ...topSolutions,
    {
      id: "all-solutions",
      titre: "Toutes nos solutions",
      description:
        "Parcourez l'ensemble de nos reponses metier pour identifier celle qui correspond a votre environnement.",
      slug: "/solutions",
      isAllSolutionsCard: true,
    },
  ];

  const cardsPerSlide = isMobile ? 1 : isTablet ? 2 : 3;
  const totalSlides = Math.ceil(carouselSolutions.length / cardsPerSlide);
  const lastSlideItems = carouselSolutions.length % cardsPerSlide || cardsPerSlide;
  const shouldCenterLastSlide = currentSlide === totalSlides - 1 && lastSlideItems < cardsPerSlide;
  const lastSlideOffset = shouldCenterLastSlide
    ? ((cardsPerSlide - lastSlideItems) * (100 / cardsPerSlide)) / 2
    : 0;

  useEffect(() => {
    if (currentSlide > totalSlides - 1) {
      setCurrentSlide(0);
    }
  }, [totalSlides, currentSlide]);

  useEffect(() => {
    if (totalSlides <= 1 || isHovering) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setCurrentSlide((prev) => (prev >= totalSlides - 1 ? 0 : prev + 1));
    }, 3000);

    return () => window.clearInterval(timer);
  }, [totalSlides, isHovering]);

  const sectionStats = useMemo(
    () => [
      { value: "6+", label: "Univers metier" },
      { value: "360°", label: "Approche terrain" },
      { value: "24/7", label: "Continuite operationnelle" },
    ],
    []
  );

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        py: { xs: 9, md: 13 },
        background: `
          radial-gradient(900px 500px at 10% 10%, rgba(249,168,54,0.16), transparent 60%),
          radial-gradient(700px 420px at 88% 18%, rgba(18,52,101,0.18), transparent 58%),
          linear-gradient(180deg, #f8fbff 0%, #eef4fb 52%, #e9f1fb 100%)
        `,
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(90deg, rgba(255,255,255,0.48) 0%, rgba(255,255,255,0.08) 40%, rgba(255,255,255,0) 100%)",
          pointerEvents: "none",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          top: { xs: -180, md: -220 },
          right: { xs: -180, md: -130 },
          width: { xs: 360, md: 520 },
          height: { xs: 360, md: 520 },
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(249,168,54,0.22) 0%, rgba(249,168,54,0.05) 42%, rgba(249,168,54,0) 72%)",
          filter: "blur(8px)",
          pointerEvents: "none",
        },
      }}
    >
      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        <Fade in appear={false} timeout={600}>
          <Box
            sx={{
              mb: { xs: 5.5, md: 7.5 },
            }}
          >
            <Box
              sx={{
                maxWidth: 980,
                mx: "auto",
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.2,
                  px: 2.1,
                  py: 1,
                  borderRadius: "999px",
                  border: "1px solid rgba(18,52,101,0.10)",
                  bgcolor: "rgba(255,255,255,0.75)",
                  boxShadow: "0 14px 35px rgba(18,52,101,0.08)",
                  backdropFilter: "blur(10px)",
                  mb: 2.5,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: COLORS.secondary,
                    boxShadow: "0 0 0 6px rgba(249,168,54,0.16)",
                  }}
                />
                <Typography
                  sx={{
                    color: COLORS.primary,
                    fontSize: "0.78rem",
                    fontWeight: 800,
                    letterSpacing: 1.2,
                    textTransform: "uppercase",
                  }}
                >
                  Solutions Teledyne
                </Typography>
              </Box>

              <Typography
                variant="h2"
                sx={{
                  color: COLORS.dark,
                  fontWeight: 900,
                  fontSize: { xs: "2.2rem", sm: "2.9rem", md: "4rem" },
                  lineHeight: { xs: 1.12, md: 1.02 },
                  letterSpacing: { xs: -1, md: -2 },
                  maxWidth: 920,
                  mx: "auto",
                  mb: 2.2,
                  textWrap: "balance",
                }}
              >
                Des solutions pensees pour{" "}
                <Box component="span" sx={{ color: COLORS.primary }}>
                  chaque environnement metier
                </Box>
              </Typography>

              <Typography
                sx={{
                  color: COLORS.muted,
                  fontSize: { xs: "1rem", md: "1.08rem" },
                  lineHeight: 1.9,
                  maxWidth: 760,
                  mx: "auto",
                  fontWeight: 500,
                }}
              >
                Decouvrez des solutions adaptees a vos exigences terrain pour
                l industrie, la sante, la distribution, la logistique et les
                services, avec un design d usage plus clair, plus rapide et plus
                impactant.
              </Typography>

              <Box
                sx={{
                  mt: { xs: 3.5, md: 4.5 },
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: 1.2,
                }}
              >
                {sectionStats.map((stat) => (
                  <Box
                    key={stat.label}
                    sx={{
                      minWidth: { xs: 110, md: 140 },
                      px: { xs: 1.8, md: 2.1 },
                      py: { xs: 1.2, md: 1.35 },
                      borderRadius: "18px",
                      bgcolor: "rgba(255,255,255,0.72)",
                      border: "1px solid rgba(18,52,101,0.08)",
                      boxShadow: "0 12px 28px rgba(18,52,101,0.06)",
                      backdropFilter: "blur(8px)",
                    }}
                  >
                    <Typography
                      sx={{
                        color: COLORS.primary,
                        fontWeight: 900,
                        fontSize: { xs: "1rem", md: "1.15rem" },
                        lineHeight: 1.1,
                        mb: 0.4,
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      sx={{
                        color: "rgba(9,26,51,0.70)",
                        fontSize: "0.77rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Fade>

        <>
            {topSolutions.length > 0 ? (
              <Fade in appear={false} timeout={750}>
                <Box
                  sx={{
                    position: "relative",
                    px: { xs: 0, md: 5 },
                  }}
                  onMouseEnter={() => setIsHovering(true)}
                  onMouseLeave={() => setIsHovering(false)}
                >
                  <Box
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      borderRadius: { xs: "28px", md: "34px" },
                      p: { xs: 1, md: 1.2 },
                      bgcolor: "rgba(255,255,255,0.42)",
                      border: "1px solid rgba(255,255,255,0.65)",
                      boxShadow: "0 26px 70px rgba(18,52,101,0.10)",
                      backdropFilter: "blur(12px)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        transition: "transform 0.65s cubic-bezier(0.22, 1, 0.36, 1)",
                        transform: `translateX(calc(-${currentSlide * 100}% + ${lastSlideOffset}%))`,
                      }}
                    >
                      {carouselSolutions.map((item, index) => {
                        const title = item.titre || item.title || "Solution metier";
                        const description =
                          item.description ||
                          "Decouvrez une solution adaptee a vos operations et a vos usages terrain.";
                        const image = item.image;
                        const link = item.slug || "#";
                        const isAllSolutionsCard = item.isAllSolutionsCard;

                        return (
                          <Box
                            key={item.id || item.idArticle || title}
                            sx={{
                              minWidth: `${100 / cardsPerSlide}%`,
                              maxWidth: `${100 / cardsPerSlide}%`,
                              p: { xs: 1, md: 1.35 },
                              opacity: 0,
                              animation: "homeSolutionCardReveal 0.7s ease forwards",
                              animationDelay: `${Math.min(index ?? 0, 5) * 100}ms`,
                              "@keyframes homeSolutionCardReveal": {
                                from: {
                                  opacity: 0,
                                  transform: "translateY(34px)",
                                },
                                to: {
                                  opacity: 1,
                                  transform: "translateY(0)",
                                },
                              },
                            }}
                          >
                            <Card
                              sx={{
                                height: "100%",
                                borderRadius: { xs: "24px", md: "28px" },
                                overflow: "hidden",
                                position: "relative",
                                bgcolor: "transparent",
                                boxShadow: "0 20px 50px rgba(10,25,50,0.14)",
                                border: "1px solid rgba(255,255,255,0.16)",
                              }}
                            >
                              {isAllSolutionsCard ? (
                                <CardActionArea
                                  component={NavLink}
                                  to="/solutions"
                                  onClick={() => window.scrollTo(0, 0)}
                                  sx={{
                                    position: "relative",
                                    display: "flex",
                                    height: { xs: 360, sm: 400, md: 460 },
                                    textDecoration: "none",
                                    overflow: "hidden",
                                    p: { xs: 2.4, md: 3 },
                                    alignItems: "flex-end",
                                    background: `
                                      radial-gradient(420px 260px at 20% 20%, rgba(249,168,54,0.34), transparent 62%),
                                      linear-gradient(145deg, ${COLORS.primary} 0%, ${COLORS.navy} 58%, #07152b 100%)
                                    `,
                                    "&:hover .all-solutions-arrow": {
                                      transform: "translate(4px, -4px)",
                                      bgcolor: COLORS.white,
                                    },
                                    "&:hover .all-solutions-line": {
                                      width: 58,
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      inset: 0,
                                      background:
                                        "linear-gradient(135deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.04) 42%, rgba(255,255,255,0) 70%)",
                                      pointerEvents: "none",
                                    }}
                                  />

                                  <Box sx={{ position: "relative", zIndex: 1 }}>
                                    <Box
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 1.6,
                                      }}
                                    >
                                      <Box
                                        className="all-solutions-line"
                                        sx={{
                                          width: 34,
                                          height: 2,
                                          borderRadius: "999px",
                                          bgcolor: COLORS.secondary,
                                          transition: "width 0.25s ease",
                                        }}
                                      />
                                      <Typography
                                        sx={{
                                          color: "rgba(255,255,255,0.78)",
                                          fontWeight: 700,
                                          fontSize: "0.78rem",
                                          textTransform: "uppercase",
                                          letterSpacing: 1.2,
                                        }}
                                      >
                                        Explorer
                                      </Typography>
                                    </Box>

                                    <Typography
                                      sx={{
                                        color: COLORS.white,
                                        fontWeight: 900,
                                        fontSize: { xs: "1.8rem", md: "2.1rem" },
                                        lineHeight: 1.08,
                                        mb: 1.5,
                                        maxWidth: 280,
                                      }}
                                    >
                                      Toutes nos solutions
                                    </Typography>

                                    <Typography
                                      sx={{
                                        color: "rgba(255,255,255,0.78)",
                                        fontSize: { xs: "0.92rem", md: "0.96rem" },
                                        lineHeight: 1.7,
                                        maxWidth: "85%",
                                      }}
                                    >
                                      {description}
                                    </Typography>

                                    <Box
                                      sx={{
                                        mt: 2.4,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1.2,
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          color: COLORS.secondary,
                                          fontWeight: 800,
                                          fontSize: "0.9rem",
                                        }}
                                      >
                                        Voir la page solutions
                                      </Typography>

                                      <Box
                                        className="all-solutions-arrow"
                                        sx={{
                                          width: 52,
                                          height: 52,
                                          borderRadius: "16px",
                                          bgcolor: COLORS.secondary,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          flexShrink: 0,
                                          transition:
                                            "transform 0.25s ease, background-color 0.25s ease",
                                        }}
                                      >
                                        <ArrowForward
                                          sx={{
                                            color: COLORS.primary,
                                            fontSize: 22,
                                            fontWeight: 900,
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                  </Box>
                                </CardActionArea>
                              ) : (
                                <CardActionArea
                                  component={NavLink}
                                  to={link}
                                  onClick={() => window.scrollTo(0, 0)}
                                  sx={{
                                    position: "relative",
                                    display: "block",
                                    height: { xs: 360, sm: 400, md: 460 },
                                    textDecoration: "none",
                                    overflow: "hidden",
                                    "&:hover .sol-image": {
                                      transform: "scale(1.09)",
                                    },
                                    "&:hover .sol-base-overlay": {
                                      opacity: 0.88,
                                    },
                                    "&:hover .sol-hover-overlay": {
                                      opacity: 1,
                                    },
                                    "&:hover .sol-content": {
                                      transform: "translateY(-6px)",
                                    },
                                    "&:hover .sol-arrow-wrap": {
                                      transform: "translateY(0) scale(1)",
                                      opacity: 1,
                                    },
                                    "&:hover .sol-card-shine": {
                                      opacity: 1,
                                      transform: "translateX(0)",
                                    },
                                  }}
                                >
                                  <Box
                                    className="sol-image"
                                    component="img"
                                    src={image}
                      srcSet={responsiveImages[image]}
                      sizes="(max-width: 899px) calc(100vw - 32px), 560px"
                      decoding="async"
                                    alt={title}
                                    loading="lazy"
                                    sx={{
                                      position: "absolute",
                                      inset: 0,
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      transition: "transform 0.9s cubic-bezier(0.22, 1, 0.36, 1)",
                                    }}
                                  />

                                <Box
                                  className="sol-base-overlay"
                                  sx={{
                                    position: "absolute",
                                    inset: 0,
                                    opacity: 1,
                                    transition: "opacity 0.45s ease",
                                    background: `
                                      linear-gradient(180deg, rgba(5,15,32,0.10) 0%, rgba(5,15,32,0.22) 28%, rgba(5,15,32,0.72) 76%, rgba(5,15,32,0.92) 100%)
                                    `,
                                  }}
                                />

                                <Box
                                  className="sol-hover-overlay"
                                  sx={{
                                    position: "absolute",
                                    inset: 0,
                                    opacity: 0,
                                    transition: "opacity 0.45s ease",
                                    background: `
                                      linear-gradient(180deg, rgba(18,52,101,0.14) 0%, rgba(18,52,101,0.34) 40%, rgba(9,26,51,0.88) 100%)
                                    `,
                                  }}
                                />

                                <Box
                                  className="sol-card-shine"
                                  sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "55%",
                                    height: "100%",
                                    opacity: 0,
                                    transform: "translateX(-18px)",
                                    transition: "all 0.45s ease",
                                    background:
                                      "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.06) 35%, rgba(255,255,255,0) 70%)",
                                    pointerEvents: "none",
                                  }}
                                />

                                <Box
                                  sx={{
                                    position: "absolute",
                                    inset: 0,
                                    borderRadius: "inherit",
                                    border: "1px solid rgba(255,255,255,0.18)",
                                    pointerEvents: "none",
                                  }}
                                />

                                

                                <Box
                                  className="sol-content"
                                  sx={{
                                    position: "absolute",
                                    inset: 0,
                                    zIndex: 2,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "flex-end",
                                    p: { xs: 2.3, md: 3 },
                                    transition: "transform 0.38s ease",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 1,
                                      mb: 1.4,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 34,
                                        height: 2,
                                        borderRadius: "999px",
                                        bgcolor: COLORS.secondary,
                                      }}
                                    />
                                    <Typography
                                      sx={{
                                        color: "rgba(255,255,255,0.78)",
                                        fontWeight: 700,
                                        fontSize: "0.78rem",
                                        textTransform: "uppercase",
                                        letterSpacing: 1.2,
                                      }}
                                    >
                                      {title}
                                    </Typography>
                                  </Box>

                                  <Typography
                                    sx={{
                                      color: "rgba(255,255,255,0.82)",
                                      fontSize: { xs: "0.92rem", md: "0.96rem" },
                                      lineHeight: 1.7,
                                      maxWidth: "85%",
                                    }}
                                  >
                                    {description}
                                  </Typography>

                                  <Box
                                    sx={{
                                      mt: 2.2,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 1.2,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        color: COLORS.secondary,
                                        fontWeight: 800,
                                        fontSize: "0.9rem",
                                        letterSpacing: 0.2,
                                      }}
                                    >
                                      En savoir plus
                                    </Typography>

                                    <Box
                                      className="sol-arrow-wrap"
                                      sx={{
                                        width: 52,
                                        height: 52,
                                        borderRadius: "16px",
                                        bgcolor: COLORS.secondary,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                        boxShadow: "0 16px 34px rgba(249,168,54,0.38)",
                                        opacity: isMobile ? 1 : 0,
                                        transform: isMobile
                                          ? "translateY(0) scale(1)"
                                          : "translateY(10px) scale(0.94)",
                                        transition:
                                          "transform 0.35s ease, opacity 0.35s ease, box-shadow 0.35s ease",
                                      }}
                                    >
                                      <ArrowOutward
                                        sx={{
                                          color: COLORS.primary,
                                          fontSize: 22,
                                          fontWeight: 900,
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                </Box>
                                </CardActionArea>
                              )}
                            </Card>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>

                  {totalSlides > 1 && (
                    <>
                      <IconButton
                        aria-label="Solution précédente"
                        onClick={() => setCurrentSlide((prev) => Math.max(0, prev - 1))}
                        disabled={currentSlide === 0}
                        sx={{
                          position: "absolute",
                          display: { xs: "none", md: "inline-flex" },
                          left: { md: 0 },
                          top: { md: "50%" },
                          bottom: { md: "auto" },
                          transform: { xs: "none", md: "translateY(-50%)" },
                          width: { md: 54 },
                          height: { md: 54 },
                          bgcolor: { md: "rgba(255,255,255,0.92)" },
                          color: COLORS.primary,
                          border: { md: "1px solid rgba(18,52,101,0.10)" },
                          boxShadow: "0 18px 38px rgba(18,52,101,0.18)",
                          opacity: isMobile || isHovering ? 1 : 0,
                          transition: "all 0.3s ease",
                          zIndex: 12,
                          backdropFilter: "blur(10px)",
                          "& svg": { color: { md: COLORS.primary } },
                          "&:hover": {
                            bgcolor: COLORS.secondary,
                            transform: { xs: "scale(1.05)", md: "translateY(-50%) scale(1.05)" },
                            boxShadow: "0 18px 38px rgba(249,168,54,0.34)",
                          },
                          "&.Mui-disabled": {
                            opacity: 0.3,
                            color: "rgba(18,52,101,0.48)",
                          },
                        }}
                      >
                        <ChevronLeft />
                      </IconButton>

                      <IconButton
                        aria-label="Solution suivante"
                        onClick={() =>
                          setCurrentSlide((prev) => Math.min(totalSlides - 1, prev + 1))
                        }
                        disabled={currentSlide === totalSlides - 1}
                        sx={{
                          position: "absolute",
                          display: { xs: "none", md: "inline-flex" },
                          right: { md: 0 },
                          top: { md: "50%" },
                          bottom: { md: "auto" },
                          transform: { xs: "none", md: "translateY(-50%)" },
                          width: { md: 54 },
                          height: { md: 54 },
                          bgcolor: { md: "rgba(255,255,255,0.92)" },
                          color: COLORS.primary,
                          border: { md: "1px solid rgba(18,52,101,0.10)" },
                          boxShadow: "0 18px 38px rgba(18,52,101,0.18)",
                          opacity: isMobile || isHovering ? 1 : 0,
                          transition: "all 0.3s ease",
                          zIndex: 12,
                          backdropFilter: "blur(10px)",
                          "& svg": { color: { md: COLORS.primary } },
                          "&:hover": {
                            bgcolor: COLORS.secondary,
                            transform: { xs: "scale(1.05)", md: "translateY(-50%) scale(1.05)" },
                            boxShadow: "0 18px 38px rgba(249,168,54,0.34)",
                          },
                          "&.Mui-disabled": {
                            opacity: 0.3,
                            color: "rgba(18,52,101,0.48)",
                          },
                        }}
                      >
                        <ChevronRight />
                      </IconButton>
                    </>
                  )}

                  {totalSlides > 1 && (
                    <Box
                      sx={{
                        mt: 3.5,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {isMobile && (
                        <IconButton
                          aria-label="Solution précédente"
                        onClick={() => setCurrentSlide((prev) => Math.max(0, prev - 1))}
                          disabled={currentSlide === 0}
                          sx={{
                            width: 42,
                            height: 42,
                            bgcolor: "rgba(255,255,255,0.92)",
                            color: COLORS.primary,
                            border: "1px solid rgba(18,52,101,0.10)",
                            boxShadow: "0 10px 24px rgba(18,52,101,0.14)",
                            flexShrink: 0,
                            "&:hover": {
                              bgcolor: COLORS.secondary,
                              transform: "scale(1.05)",
                            },
                            "&.Mui-disabled": {
                              opacity: 0.35,
                              color: "rgba(18,52,101,0.48)",
                            },
                          }}
                        >
                          <ChevronLeft />
                        </IconButton>
                      )}

                      {Array.from({ length: totalSlides }).map((_, idx) => (
                        <Box
                          key={idx}
                          onClick={() => setCurrentSlide(idx)}
                          sx={{
                            width: currentSlide === idx ? 36 : 10,
                            height: 10,
                            borderRadius: "999px",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            bgcolor:
                              currentSlide === idx
                                ? COLORS.secondary
                                : "rgba(18,52,101,0.16)",
                            boxShadow:
                              currentSlide === idx
                                ? "0 8px 20px rgba(249,168,54,0.32)"
                                : "none",
                            "&:hover": {
                              bgcolor:
                                currentSlide === idx
                                  ? COLORS.secondary
                                  : "rgba(18,52,101,0.34)",
                            },
                          }}
                        />
                      ))}

                      {isMobile && (
                        <IconButton
                          aria-label="Solution suivante"
                        onClick={() =>
                            setCurrentSlide((prev) => Math.min(totalSlides - 1, prev + 1))
                          }
                          disabled={currentSlide === totalSlides - 1}
                          sx={{
                            width: 42,
                            height: 42,
                            bgcolor: "rgba(255,255,255,0.92)",
                            color: COLORS.primary,
                            border: "1px solid rgba(18,52,101,0.10)",
                            boxShadow: "0 10px 24px rgba(18,52,101,0.14)",
                            flexShrink: 0,
                            "&:hover": {
                              bgcolor: COLORS.secondary,
                              transform: "scale(1.05)",
                            },
                            "&.Mui-disabled": {
                              opacity: 0.35,
                              color: "rgba(18,52,101,0.48)",
                            },
                          }}
                        >
                          <ChevronRight />
                        </IconButton>
                      )}
                    </Box>
                  )}
                </Box>
              </Fade>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  height: 240,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress
                  size={62}
                  thickness={4.2}
                  sx={{ color: COLORS.secondary }}
                />
              </Box>
            )}
        </>

      </Container>
    </Box>
  );
}
